// Collection Tabs section JS. Drives tab switching, paginated grid display
// with infinite-loop arrow nav, sort, swipe, and breakpoint-aware page sizing.
//
// Cohesion notes:
// - Section element is found via `[data-collection-tabs]` attribute (not
//   `[class*="collection-tabs-"]` partial-match — that selector matched any
//   element with a class starting with that prefix).
// - Button class hooks are `.swiper-btn-prev` / `.swiper-btn-next` to align
//   with the rest of the codebase. The buttons aren't Swiper navigation —
//   their click handlers are wired manually here — but the naming convention
//   keeps slider-nav classes consistent across sections.
// - Buttons hide via `.is-nav-hidden` class on the panel when the current
//   viewport's pagination math yields <= 1 page. This per-panel tracking
//   matters because tabs can have different product counts.

(function () {
  'use strict';

  class CollectionTabsSection {
    constructor(container) {
      this.container = container;
      this.tabButtons = Array.from(container.querySelectorAll('.collection-tabs__tab'));
      this.panels = Array.from(container.querySelectorAll('.collection-tabs__panel'));
      this.currentBreakpoint = this.getBreakpoint();
      this.resizeHandler = null;
      this.init();
    }

    init() {
      this.setupTabs();
      const firstPanel = this.panels[0];
      if (firstPanel) {
        this.initializePanel(firstPanel);
      }

      // Recalculate pagination on breakpoint changes (rotation, resize).
      // Stored on the instance so destroy() can remove it cleanly.
      let resizeTimer;
      this.resizeHandler = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          const newBreakpoint = this.getBreakpoint();
          if (newBreakpoint !== this.currentBreakpoint) {
            this.currentBreakpoint = newBreakpoint;
            const activePanel = this.container.querySelector('.collection-tabs__panel.is-active');
            if (activePanel) {
              const currentPage = activePanel.currentPage || 1;
              this.initializePanel(activePanel, true);
              this.showPage(activePanel, Math.min(currentPage, activePanel.totalPages), 'next', true);
            }
          }
        }, 250);
      };
      window.addEventListener('resize', this.resizeHandler);
    }

    destroy() {
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
        this.resizeHandler = null;
      }
    }

    getBreakpoint() {
      const width = window.innerWidth;
      if (width < 750) return 'mobile';
      if (width < 990) return 'tablet';
      return 'desktop';
    }

    getProductsPerPage(grid) {
      const maxRows = parseInt(grid.dataset.maxRows);
      const w = window.innerWidth;
      let perRow;
      if (w < 750) {
        perRow = parseInt(grid.dataset.productsPerRowMobile);
      } else if (w < 990) {
        perRow = parseInt(grid.dataset.productsPerRowTablet);
      } else {
        perRow = parseInt(grid.dataset.productsPerRowDesktop);
      }
      return perRow * maxRows;
    }

    sortProducts(grid, cards, sortBy) {
      const sorted = [...cards];
      switch (sortBy) {
        case 'metafield_desc':
          sorted.sort((a, b) => (parseFloat(b.dataset.metafieldValue) || 0) - (parseFloat(a.dataset.metafieldValue) || 0));
          break;
        case 'metafield_asc':
          sorted.sort((a, b) => (parseFloat(a.dataset.metafieldValue) || 0) - (parseFloat(b.dataset.metafieldValue) || 0));
          break;
        case 'price_asc':
          sorted.sort((a, b) => parseFloat(a.dataset.price) - parseFloat(b.dataset.price));
          break;
        case 'price_desc':
          sorted.sort((a, b) => parseFloat(b.dataset.price) - parseFloat(a.dataset.price));
          break;
        case 'title_asc':
          sorted.sort((a, b) => a.dataset.title.localeCompare(b.dataset.title));
          break;
        case 'title_desc':
          sorted.sort((a, b) => b.dataset.title.localeCompare(a.dataset.title));
          break;
        case 'created_asc':
          sorted.sort((a, b) => parseInt(a.dataset.created) - parseInt(b.dataset.created));
          break;
        case 'created_desc':
          sorted.sort((a, b) => parseInt(b.dataset.created) - parseInt(a.dataset.created));
          break;
        default:
          return;
      }
      sorted.forEach((card) => grid.appendChild(card));
    }

    setupTabs() {
      this.tabButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.switchTab(button.dataset.tabId);
        });
      });
    }

    switchTab(tabId) {
      this.tabButtons.forEach((btn) => {
        const isActive = btn.dataset.tabId === tabId;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      this.panels.forEach((panel) => {
        const isActive = panel.dataset.panelId === tabId;
        panel.classList.toggle('is-active', isActive);
        if (isActive) {
          this.initializePanel(panel);
        }
      });
    }

    initializePanel(panel, skipInitialShow = false) {
      const grid = panel.querySelector('.collection-tabs__grid');
      if (!grid) return;

      const cards = Array.from(panel.querySelectorAll('.product-card'));
      const sortBy = grid.dataset.sortBy;
      if (sortBy) {
        this.sortProducts(grid, cards, sortBy);
      }

      const productsPerPage = this.getProductsPerPage(grid);
      const totalPages = Math.ceil(cards.length / productsPerPage);
      panel.totalPages = totalPages;

      const totalSpan = panel.querySelector('.collection-tabs__total-pages');
      if (totalSpan) {
        totalSpan.textContent = totalPages;
      }

      // Hide nav buttons (and the page indicator) when there's only one
      // page worth of products at the current viewport. The panel-level
      // class gates both via CSS — buttons display:none, indicator stays
      // hidden because there's nothing to indicate.
      panel.classList.toggle('is-nav-hidden', totalPages <= 1);

      // Wire arrow buttons. Replace-with-clone resets any prior listeners
      // so re-initialization (tab switch, breakpoint change) doesn't stack
      // duplicate handlers.
      const oldPrev = panel.querySelector('.swiper-btn-prev');
      const oldNext = panel.querySelector('.swiper-btn-next');
      if (oldPrev && oldNext) {
        oldPrev.replaceWith(oldPrev.cloneNode(true));
        oldNext.replaceWith(oldNext.cloneNode(true));

        const prev = panel.querySelector('.swiper-btn-prev');
        const next = panel.querySelector('.swiper-btn-next');

        prev.addEventListener('click', () => {
          const newPage = panel.currentPage > 1 ? panel.currentPage - 1 : panel.totalPages;
          this.showPage(panel, newPage, 'prev');
        });
        next.addEventListener('click', () => {
          const newPage = panel.currentPage < panel.totalPages ? panel.currentPage + 1 : 1;
          this.showPage(panel, newPage, 'next');
        });
      }

      // Mini-buttons inside the page indicator (mobile-only via CSS).
      // Same pagination logic as the big buttons; they're separate DOM
      // elements with separate listeners since CSS visibility differs.
      const oldMiniPrev = panel.querySelector('.collection-tabs__mini-btn--prev');
      const oldMiniNext = panel.querySelector('.collection-tabs__mini-btn--next');
      if (oldMiniPrev && oldMiniNext) {
        oldMiniPrev.replaceWith(oldMiniPrev.cloneNode(true));
        oldMiniNext.replaceWith(oldMiniNext.cloneNode(true));

        const miniPrev = panel.querySelector('.collection-tabs__mini-btn--prev');
        const miniNext = panel.querySelector('.collection-tabs__mini-btn--next');

        miniPrev.addEventListener('click', () => {
          const newPage = panel.currentPage > 1 ? panel.currentPage - 1 : panel.totalPages;
          this.showPage(panel, newPage, 'prev');
        });
        miniNext.addEventListener('click', () => {
          const newPage = panel.currentPage < panel.totalPages ? panel.currentPage + 1 : 1;
          this.showPage(panel, newPage, 'next');
        });
      }

      this.setupTouchSwipe(grid, panel);

      if (!skipInitialShow) {
        this.showPage(panel, 1, 'next');
      }
    }

    showPage(panel, pageNum, direction = 'next', force = false) {
      const grid = panel.querySelector('.collection-tabs__grid');
      const cards = Array.from(panel.querySelectorAll('.product-card'));
      const currentSpan = panel.querySelector('.collection-tabs__current-page');
      const productsPerPage = this.getProductsPerPage(grid);
      const oldPage = panel.currentPage || null;
      panel.currentPage = pageNum;

      const newStart = (pageNum - 1) * productsPerPage;
      const newEnd = newStart + productsPerPage;

      // First render — no animation, just show/hide.
      if (oldPage === null) {
        cards.forEach((card, i) => {
          if (i >= newStart && i < newEnd) {
            card.style.display = 'block';
            card.style.opacity = '1';
          } else {
            card.style.display = 'none';
          }
        });
        if (currentSpan) currentSpan.textContent = pageNum;
        return;
      }

      const oldStart = (oldPage - 1) * productsPerPage;
      const oldEnd = oldStart + productsPerPage;

      // Same page, no force — nothing to do.
      if (oldPage === pageNum && !force) return;

      // Same page, forced — refresh visible card set without animation.
      // Used after breakpoint change when the products-per-page count shifts.
      if (oldPage === pageNum && force) {
        cards.forEach((card) => {
          card.style.display = 'none';
          card.style.opacity = '0';
          card.classList.remove('is-slide-in-right', 'is-slide-in-left', 'is-slide-out-left', 'is-slide-out-right');
        });
        cards.forEach((card, i) => {
          if (i >= newStart && i < newEnd) {
            card.style.display = 'block';
            card.style.opacity = '1';
          }
        });
        if (currentSpan) currentSpan.textContent = pageNum;
        return;
      }

      const slideOutClass = direction === 'next' ? 'is-slide-out-left' : 'is-slide-out-right';
      const slideInClass = direction === 'next' ? 'is-slide-in-right' : 'is-slide-in-left';

      // 1. Slide out the old page's cards. will-change is added scoped to
      //    the transition window so we're not keeping every card layer
      //    promoted permanently.
      cards.forEach((card, i) => {
        if (i >= oldStart && i < oldEnd) {
          card.classList.remove('is-slide-in-right', 'is-slide-in-left', 'is-slide-out-left', 'is-slide-out-right');
          card.style.willChange = 'transform, opacity';
          // Force reflow so the next class addition triggers an animation.
          // eslint-disable-next-line no-unused-expressions
          card.offsetWidth;
          card.classList.add(slideOutClass);
        }
      });

      setTimeout(() => {
        cards.forEach((card, i) => {
          if (i >= oldStart && i < oldEnd) {
            card.style.display = 'none';
            card.classList.remove(slideOutClass);
            card.style.willChange = '';
          }
          if (i >= newStart && i < newEnd) {
            card.style.display = 'block';
            card.style.opacity = '0';
            card.style.willChange = 'transform, opacity';
            card.classList.remove('is-slide-in-right', 'is-slide-in-left', 'is-slide-out-left', 'is-slide-out-right');
          }
        });

        // 2. Stagger the new cards' slide-in animations.
        requestAnimationFrame(() => {
          cards.forEach((card, i) => {
            if (i >= newStart && i < newEnd) {
              const delay = (i - newStart) * 40;
              setTimeout(() => {
                card.style.opacity = '1';
                card.classList.add(slideInClass);
                // Clear will-change after the animation completes (350ms
                // per the keyframes duration).
                setTimeout(() => {
                  card.style.willChange = '';
                }, 400);
              }, delay);
            }
          });
        });
      }, 300);

      if (currentSpan) currentSpan.textContent = pageNum;
    }

    setupTouchSwipe(grid, panel) {
      if (!grid) return;
      let startX = 0;
      const minDistance = 50;

      grid.addEventListener(
        'touchstart',
        (e) => {
          startX = e.changedTouches[0].screenX;
        },
        { passive: true }
      );
      grid.addEventListener(
        'touchend',
        (e) => {
          const endX = e.changedTouches[0].screenX;
          const diff = startX - endX;
          if (Math.abs(diff) <= minDistance) return;
          // Don't swipe if pagination isn't needed.
          if (panel.totalPages <= 1) return;

          if (diff > 0) {
            const newPage = panel.currentPage < panel.totalPages ? panel.currentPage + 1 : 1;
            this.showPage(panel, newPage, 'next');
          } else {
            const newPage = panel.currentPage > 1 ? panel.currentPage - 1 : panel.totalPages;
            this.showPage(panel, newPage, 'prev');
          }
        },
        { passive: true }
      );
    }
  }

  // Track instances by section element so design-mode reloads don't
  // orphan listeners (the previous implementation created a new instance
  // on every tab block-select, leaking resize handlers).
  const instances = new WeakMap();

  function initSection(sectionEl) {
    if (!sectionEl) return;
    const existing = instances.get(sectionEl);
    if (existing) {
      existing.destroy();
    }
    instances.set(sectionEl, new CollectionTabsSection(sectionEl));
  }

  function initAll(root = document) {
    root.querySelectorAll('[data-collection-tabs]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // Theme editor lifecycle
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      initAll(event.target);
    });
    document.addEventListener('shopify:section:unload', (event) => {
      const section = event.target.querySelector('[data-collection-tabs]');
      if (section) {
        const instance = instances.get(section);
        if (instance) {
          instance.destroy();
          instances.delete(section);
        }
      }
    });
    document.addEventListener('shopify:block:select', (event) => {
      const section = event.target.closest('[data-collection-tabs]');
      if (!section) return;
      const instance = instances.get(section);
      if (instance && event.target.classList.contains('collection-tabs__tab')) {
        instance.switchTab(event.target.dataset.tabId);
      }
    });
  }
})();
